import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { IcPageHeader, IcButton } from "@ukic/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { api, type EntityTimeline } from "../api";
import { timeAgo } from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";

export default function EntityDetail() {
  const { id: rawId } = useParams();
  const id = decodeURIComponent(rawId || "");
  const [data, setData] = useState<EntityTimeline | null>(null);
  const [error, setError] = useState<unknown>(null);
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.getEntity(id, 1000).then(setData).catch(setError);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // init map once
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { worldCopyJump: true }).setView([30, 0], 2);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors", maxZoom: 18,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []);

  // draw the track when data loads
  useEffect(() => {
    const lg = layerRef.current;
    if (!lg || !data || !mapRef.current) return;
    lg.clearLayers();
    const pts = data.track.map((t) => [t.lat, t.lon] as [number, number]);
    if (!pts.length) return;
    // path (chronological), points, and a radius ring on the latest fix.
    L.polyline(pts, { color: "#1d70b8", weight: 2, opacity: 0.6 }).addTo(lg);
    data.track.forEach((t, i) => {
      const latest = i === data.track.length - 1;
      if (latest && t.radius_km) {
        L.circle([t.lat, t.lon], { radius: t.radius_km * 1000, color: "#d4351c", weight: 1, fillOpacity: 0.06, interactive: false }).addTo(lg);
      }
      L.circleMarker([t.lat, t.lon], {
        radius: latest ? 7 : 4, color: latest ? "#d4351c" : "#1d70b8",
        fillColor: latest ? "#d4351c" : "#1d70b8", fillOpacity: 0.9, weight: 1,
      }).bindPopup(`${t.lat.toFixed(4)},${t.lon.toFixed(4)}${t.radius_km ? ` &plusmn;${t.radius_km}km` : ""}<br/><small>${timeAgo(t.ts)}</small>`).addTo(lg);
    });
    mapRef.current.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 9 });
  }, [data]);

  type Rec = EntityTimeline["records"][number];
  const cols: Column<Rec>[] = [
    { key: "ts", header: "Time", cell: (r) => timeAgo(r.ts) },
    { key: "protocol", header: "Type", cell: (r) => <code>{r.protocol || r.format}</code> },
    { key: "content", header: "Content", cell: (r) => r.text || r.label || <span className="hl-muted">—</span> },
    { key: "pos", header: "Position", cell: (r) => (typeof r.lat === "number" ? <code>{r.lat.toFixed(2)},{r.lon!.toFixed(2)}</code> : <span className="hl-muted">—</span>) },
    { key: "sensor", header: "Sensor", cell: (r) => r.device_id },
  ];

  const recordsNewestFirst = data ? [...data.records].reverse() : [];

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading={`Entity: ${id}`} subheading="Full history — messages, signalling, and position track">
        <Link slot="actions" to={`/signals?q=${encodeURIComponent(id)}`}><IcButton variant="tertiary">Raw in Signals</IcButton></Link>
        <IcButton slot="actions" variant="secondary" onClick={load}>Refresh</IcButton>
      </IcPageHeader>

      {error ? <ErrorState error={error} onRetry={load} /> : !data ? <Loading label="Loading history…" /> : (
        <>
          <div className="hl-panel hl-row" style={{ gap: 24, flexWrap: "wrap" }}>
            <span><strong>{data.count}</strong> records</span>
            <span>first seen {data.first_seen ? timeAgo(data.first_seen) : "—"}</span>
            <span>last seen {data.last_seen ? timeAgo(data.last_seen) : "—"}</span>
            <span>{data.sensors.length} sensor(s)</span>
            <span>{data.track.length} position fix(es)</span>
          </div>

          {data.track.length > 0 && (
            <div ref={mapEl} style={{ height: "42vh", width: "100%", borderRadius: 4, zIndex: 0 }} />
          )}

          <div className="hl-panel hl-stack">
            <h3>Timeline ({data.count})</h3>
            <DataTable caption="Timeline" rows={recordsNewestFirst} columns={cols} rowKey={(r) => `${r.ts}-${r.protocol}-${r.label?.slice(0, 12)}`} />
          </div>
        </>
      )}
    </div>
  );
}
